import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, MessagesSquare, Contact, Users2, WifiOff } from 'lucide-react'
import { AiSparkle } from '@/components/AiSparkle'
import { Button } from '@/components/ui/button'
import { UserAvatarMenu } from './UserAvatarMenu'
import { ThemeSwitcher } from '@/components/ui/theme-switcher'
import { DensityToggle } from '@/components/DensityToggle'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { TranslationToggle } from '@/components/TranslationToggle'
import { TimezoneDisplayToggle } from '@/components/TimezoneDisplayToggle'
import { ContactsPanel } from './ContactsPanel'
import { OnlineUsersPanel } from './OnlineUsersPanel'
import { Logo } from '@/components/Logo'
import { useUser } from '@/app/store/auth'
import { useCan } from '@/hooks/useCan'
import { useTimezoneDisplay } from '@/contexts/TimezoneDisplayContext'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'
import { usePresence } from '@/contexts/PresenceContext'
import { useChatNotificationStore } from '@/app/store/chatNotifications'
import { SupportDropdown } from './SupportDropdown'
import { TaskbarTicker } from '@/features/ticker/components/TaskbarTicker'
import { TickerMenu } from '@/features/ticker/components/TickerMenu'

export function Topbar() {
    const user = useUser()
    const { open: openAiChat, isOpen: aiChatOpen } = useAiChatPanel()
    const { displayMode: timezoneDisplayMode, setDisplayMode: setTimezoneDisplayMode } = useTimezoneDisplay()
    const [contactsOpen, setContactsOpen] = useState(false)
    const { t } = useTranslation()
    const [onlineUsersOpen, setOnlineUsersOpen] = useState(false)
    const [supportOpen, setSupportOpen] = useState(false)
    const { onlineUsers, isConnected, connectionError, retryConnection } = usePresence()
    const onlineCount = onlineUsers.size
    const unreadChatCount = useChatNotificationStore((s) => s.unreadCount)
    const agentUnread = useChatNotificationStore((s) => s.agentUnread)
    // The chat panel - Clara and the people list of User Chat alike - is /api/ai/*, which needs
    // ai.use; the contacts panel needs contacts.read. Without them the buttons are not offered.
    const canUseAi = useCan('ai.use')
    const canReadContacts = useCan('contacts.read')

    if (!user) {
        return null
    }

    return (
        <header className="flex items-center justify-between border-b bg-background px-6 h-[var(--topbar-height)]">
            {/* Logo — click to open support dropdown */}
            <div className="flex-shrink-0 mr-6 cursor-pointer" onClick={() => setSupportOpen(!supportOpen)} title="VaultSentinel Support">
                <Logo />
            </div>

            {/* Recovery ticker conveyor: fills the gap, clipped to the bar (renders only with ticker.view) */}
            <TaskbarTicker />

            {/* Right side */}
            <div className="flex items-center space-x-2">
                {/* AI Assistant — sparkle opens the chat panel on the AI tab */}
                {canUseAi && (
                    <Button
                        variant={aiChatOpen ? 'secondary' : 'ghost'}
                        size="icon"
                        title="Clara AI Assistant and Clara Live"
                        className="relative"
                        onClick={() => openAiChat({ tab: 'agent' })}
                    >
                        <AiSparkle className="h-5 w-5 text-black dark:text-white" />
                        {agentUnread > 0 && (
                            <span className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-destructive text-[9px] font-semibold text-destructive-foreground flex items-center justify-center leading-none ring-2 ring-background">
                                {agentUnread > 9 ? '9+' : agentUnread}
                            </span>
                        )}
                    </Button>
                )}

                {/* User Chat — messages icon opens the same panel on the User Chat tab */}
                {canUseAi && (
                    <Button
                        variant={aiChatOpen ? 'secondary' : 'ghost'}
                        size="icon"
                        title="User Chat"
                        className="relative"
                        onClick={() => openAiChat({ tab: 'user' })}
                    >
                        <MessagesSquare className="h-5 w-5 text-black dark:text-white" />
                        {unreadChatCount > 0 && (
                            <span className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-destructive text-[9px] font-semibold text-destructive-foreground flex items-center justify-center leading-none ring-2 ring-background">
                                {unreadChatCount > 9 ? '9+' : unreadChatCount}
                            </span>
                        )}
                    </Button>
                )}

                {/* Notifications */}
                <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-destructive text-[8px] text-destructive-foreground flex items-center justify-center leading-none">
                        3
                    </span>
                </Button>

                {/* Online Users */}
                <Button
                    variant={onlineUsersOpen ? 'secondary' : 'ghost'}
                    size="icon"
                    title="Online Users"
                    className="relative"
                    onClick={() => { setOnlineUsersOpen(!onlineUsersOpen); setContactsOpen(false) }}
                >
                    <Users2 className="h-5 w-5" />
                    {onlineCount > 0 && (
                        <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-green-500 text-[8px] text-white flex items-center justify-center leading-none font-medium">
                            {onlineCount > 9 ? '9+' : onlineCount}
                        </span>
                    )}
                </Button>

                {/* Contacts */}
                {canReadContacts && (
                    <Button
                        variant={contactsOpen ? 'secondary' : 'ghost'}
                        size="icon"
                        title="Contacts"
                        onClick={() => { setContactsOpen(!contactsOpen); setOnlineUsersOpen(false) }}
                    >
                        <Contact className="h-5 w-5" />
                    </Button>
                )}

                {/* Live-updates connection. Renders ONLY when something is actually wrong, so it
                    is invisible in the normal case rather than another always-on green light.
                    Presence carries the online dots, unread badges and incoming call rings, so
                    when this socket is down those three things silently stop - which is exactly
                    the failure this indicator exists to stop being silent. Click to retry. */}
                {(!isConnected || connectionError) && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative text-amber-600 dark:text-amber-400"
                        onClick={retryConnection}
                        aria-label={t('presence.connectionLost', { error: connectionError || t('presence.reconnecting', { defaultValue: 'reconnecting' }) })}
                        title={t('presence.connectionLost', { error: connectionError || t('presence.reconnecting', { defaultValue: 'reconnecting' }) })}
                    >
                        <WifiOff className="h-5 w-5" />
                        <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background" />
                    </Button>
                )}

                {/* Language switcher */}
                <LanguageSwitcher />

                {/* Translation toggle */}
                <TranslationToggle />

                {/* Timezone display toggle */}
                <TimezoneDisplayToggle
                    displayMode={timezoneDisplayMode}
                    onDisplayModeChange={setTimezoneDisplayMode}
                />

                {/* Density toggle */}
                <DensityToggle />

                {/* Theme switcher */}
                <ThemeSwitcher />

                {/* Recovery ticker: on/off, detail level, recovery sets, all messages (ticker.view only) */}
                <TickerMenu />

                {/* User menu */}
                <UserAvatarMenu user={user} />
            </div>

            {/* Support dropdown */}
            <SupportDropdown open={supportOpen} onClose={() => setSupportOpen(false)} />

            {/* Online Users slide-in panel */}
            <OnlineUsersPanel open={onlineUsersOpen} onClose={() => setOnlineUsersOpen(false)} />

            {/* Contacts slide-in panel */}
            {canReadContacts && <ContactsPanel open={contactsOpen} onClose={() => setContactsOpen(false)} />}
        </header>
    )
}
